import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// Types
export interface Question {
  id: string;
  category: string;
  level: number;
  text: string;
  type: 'boolean' | 'multiple' | 'text' | 'number';
  options?: string[];
  weight: number;
  required: boolean;
}

export interface Answer {
  questionId: string;
  value: string | number | boolean;
  timestamp: Date;
}

export interface ComplianceLevel {
  id: string;
  name: string;
  minScore: number;
  maxScore: number;
  starRating: number;
  color: string;
  description: string;
}

export interface ScoreState {
  currentLevel: number;
  totalScore: number;
  maxPossibleScore: number;
  starRating: number;
  answers: Answer[];
  completedCategories: string[];
  unlockedLevels: number[];
  complianceLevel: ComplianceLevel | null;
  isVerified: boolean;
  verificationStatus: 'none' | 'pending' | 'paper' | 'visit' | 'completed';
  criticalFailures?: Answer[];
}

// Actions
type ScoreAction =
  | { type: 'ANSWER_QUESTION'; payload: Answer }
  | { type: 'CALCULATE_SCORE' }
  | { type: 'UNLOCK_LEVEL'; payload: number }
  | { type: 'SET_VERIFICATION_STATUS'; payload: ScoreState['verificationStatus'] }
  | { type: 'RESET_ASSESSMENT' };

// Compliance levels configuration
export const COMPLIANCE_LEVELS: ComplianceLevel[] = [
  {
    id: 'urgent',
    name: 'Urgent - Non-Habitable',
    minScore: -1000,
    maxScore: -1,
    starRating: 0,
    color: '#000000',
    description: 'URGENT: Critical safety issues - Property not suitable for habitation'
  },
  {
    id: 'basic',
    name: 'Basic Tenancy Compliance',
    minScore: 0,
    maxScore: 199,
    starRating: 1,
    color: '#f44336',
    description: 'Minimal compliance requirements met'
  },
  {
    id: 'bronze',
    name: 'Bronze Standard',
    minScore: 200,
    maxScore: 399,
    starRating: 2,
    color: '#cd7f32',
    description: 'Good foundation with room for improvement'
  },
  {
    id: 'silver',
    name: 'Silver Standard',
    minScore: 400,
    maxScore: 599,
    starRating: 3,
    color: '#c0c0c0',
    description: 'Above average compliance performance'
  },
  {
    id: 'gold',
    name: 'Gold Excellence',
    minScore: 600,
    maxScore: 799,
    starRating: 4,
    color: '#ffd700',
    description: 'Excellent compliance standards'
  },
  {
    id: 'platinum',
    name: 'Platinum Elite',
    minScore: 800,
    maxScore: 1000,
    starRating: 5,
    color: '#e5e4e2',
    description: 'Outstanding compliance achievement'
  }
];

// Initial state
const initialState: ScoreState = {
  currentLevel: 1,
  totalScore: 0,
  maxPossibleScore: 1000,
  starRating: 0,
  answers: [],
  completedCategories: [],
  unlockedLevels: [1],
  complianceLevel: null,
  isVerified: false,
  verificationStatus: 'none'
};

// Reducer
function scoreReducer(state: ScoreState, action: ScoreAction): ScoreState {
  switch (action.type) {
    case 'ANSWER_QUESTION':
      const existingAnswerIndex = state.answers.findIndex(
        answer => answer.questionId === action.payload.questionId
      );

      let newAnswers;
      if (existingAnswerIndex >= 0) {
        newAnswers = [...state.answers];
        newAnswers[existingAnswerIndex] = action.payload;
      } else {
        newAnswers = [...state.answers, action.payload];
      }

      return {
        ...state,
        answers: newAnswers
      };

    case 'CALCULATE_SCORE':
      // Check for critical failures first (Level 1 questions)
      const criticalFailures = state.answers.filter(answer => {
        // Critical failure conditions for each question type
        if (answer.questionId === 'epc_rating' && answer.value === false) return true;
        if (answer.questionId === 'gas_safety' && answer.value === 'No - certificate required') return true;
        if (answer.questionId === 'electrical_safety' && answer.value === false) return true;
        if (answer.questionId === 'fire_safety_compliance' && answer.value === false) return true;
        if (answer.questionId === 'hmo_license' && answer.value === 'No - not licensed') return true;
        if (answer.questionId === 'deposit_protection' && answer.value === false) return true;
        return false;
      });

      // If ANY critical failure exists, force 0 stars
      if (criticalFailures.length > 0) {
        const urgentLevel = COMPLIANCE_LEVELS.find(level => level.starRating === 0) || COMPLIANCE_LEVELS[0];
        return {
          ...state,
          totalScore: -100, // Negative score to trigger 0 stars
          starRating: 0,
          complianceLevel: urgentLevel,
          criticalFailures
        };
      }

      // Count how many Level 1 questions were answered correctly
      const level1CorrectAnswers = state.answers.filter(answer => {
        if (answer.questionId === 'epc_rating' && answer.value === true) return true;
        if (answer.questionId === 'gas_safety' && 
            (answer.value === 'Yes - valid certificate' || answer.value === 'No gas in this property')) return true;
        if (answer.questionId === 'electrical_safety' && answer.value === true) return true;
        if (answer.questionId === 'fire_safety_compliance' && answer.value === true) return true;
        if (answer.questionId === 'hmo_license' && 
            (answer.value === 'Yes - licensed HMO' || answer.value === 'Not applicable - not an HMO')) return true;
        if (answer.questionId === 'deposit_protection' && answer.value === true) return true;
        return false;
      }).length;

      // If all 6 Level 1 questions are correct, assign 1 star (Basic Tenancy Compliance)
      // Maximum possible with these questions is 1 star
      let totalScore = 0;
      let targetStarRating = 1;

      if (level1CorrectAnswers === 6) {
        totalScore = 150; // Score for 1 star (Basic Tenancy Compliance)
        targetStarRating = 1;
      } else {
        // Some critical questions missing - still 0 stars
        totalScore = -50;
        targetStarRating = 0;
      }

      // Determine compliance level
      const complianceLevel = COMPLIANCE_LEVELS.find(
        level => level.starRating === targetStarRating
      ) || COMPLIANCE_LEVELS[0];

      return {
        ...state,
        totalScore,
        starRating: targetStarRating,
        complianceLevel,
        criticalFailures: criticalFailures.length > 0 ? criticalFailures : undefined
      };

    case 'UNLOCK_LEVEL':
      if (!state.unlockedLevels.includes(action.payload)) {
        return {
          ...state,
          unlockedLevels: [...state.unlockedLevels, action.payload],
          currentLevel: Math.max(state.currentLevel, action.payload)
        };
      }
      return state;

    case 'SET_VERIFICATION_STATUS':
      return {
        ...state,
        verificationStatus: action.payload,
        isVerified: action.payload === 'completed'
      };

    case 'RESET_ASSESSMENT':
      return initialState;

    default:
      return state;
  }
}

// Context
const ScoreContext = createContext<{
  state: ScoreState;
  dispatch: React.Dispatch<ScoreAction>;
} | null>(null);

// Provider
export const ScoreProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(scoreReducer, initialState);

  return (
    <ScoreContext.Provider value={{ state, dispatch }}>
      {children}
    </ScoreContext.Provider>
  );
};

// Hook
export const useScore = () => {
  const context = useContext(ScoreContext);
  if (!context) {
    throw new Error('useScore must be used within a ScoreProvider');
  }
  return context;
};